import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../../../../api/axiosInstance";
import { ArrowRight, Search, ChevronRight, Home } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const AllCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axiosInstance.get("/users/categories");
        setCategories(res.data?.data || res.data || []);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    fetchCategories();
  }, []);

  const filteredCategories = useMemo(() => {
    return categories.filter((cat) => cat.title?.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery, categories]);

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col font-outfit">
      {/* FIXED: Scaled-down Hero Section */}
      <section className="relative pt-24 pb-28 px-6 bg-[#020617] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover" alt="bg" />
        </div>
        <div className="container mx-auto relative z-10 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-8 justify-center">
            <Link to="/home" className="hover:text-white transition-colors flex items-center gap-1">
              <Home size={14} />
              Home
            </Link>
            <ChevronRight size={14} className="opacity-50" />
            <span className="text-white">Categories</span>
          </nav>

          <div className="text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-4xl font-semibold text-white mb-8 leading-tight">
              Unlock <span className="text-blue-400">Expertise</span> Across Every Field
            </motion.h1>
            
            <div className="max-w-2xl mx-auto relative group">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input 
                type="text" 
                placeholder="Search categories..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-6 py-3.5 bg-white rounded-xl shadow-lg outline-none text-base border-2 border-transparent focus:border-[#0097b2] transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Grid */}
      <section className="container mx-auto px-6 -mt-12 pb-20 relative z-20 max-w-7xl">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => <div key={i} className="h-64 bg-white animate-pulse rounded-3xl" />)}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-3xl shadow-lg border border-slate-100">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">No categories found</h3>
            <p className="text-slate-500 mt-2">Try adjusting your search query.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <AnimatePresence>
              {filteredCategories.map((cat, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  onClick={() => navigate(`/category/${cat.id || cat._id}`)}
                  className="group bg-white p-3 rounded-[2rem] shadow-sm hover:shadow-xl transition-all border border-slate-100 cursor-pointer"
                >
                  <div className="h-44 rounded-[1.5rem] overflow-hidden mb-4 bg-slate-100">
                    <img src={cat.image} alt={cat.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="px-2 pb-2">
                    <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#134DA7] transition-colors">{cat.title}</h3>
                    <p className="text-slate-500 text-xs line-clamp-1 mt-1">{cat.description || "Expert services."}</p>
                    <div className="mt-4 pt-4 border-t flex justify-between items-center">
                      <span className="text-[9px] font-bold text-[#0097b2] uppercase tracking-widest">
                        Explore Services
                      </span>
                      <ArrowRight size={16} className="text-slate-300 group-hover:text-[#134DA7] transform transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </section>
    </div>
  );
};

export default AllCategoriesPage;